import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyBan, toCreateBan } from '../api/mappers';
import { useBanMutations } from '../hooks/use-ban-mutations';
import { useServerMessage } from '../hooks/use-server-message';
import { BAN_DURATIONS, banSchema, type BanForm } from '../schemas/ban-schema';

import { FormAlert, TextField } from './form-controls';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['visitorId', 'ip', 'reason']);

/** Ban a visitor id (from the inbox's visitor panel) or an IP address, for good or for a while. */
export function BanDialog({
  storeId,
  onOpenChange,
}: {
  storeId: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['chatSettings', 'common']);
  const serverMessage = useServerMessage();
  const { create } = useBanMutations(storeId);
  const form = useForm<BanForm>({
    resolver: useI18nResolver(banSchema),
    defaultValues: emptyBan(),
  });
  const target = useWatch({ control: form.control, name: 'target' });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await create.mutateAsync(toCreateBan(values));
      onOpenChange(false);
    } catch (error) {
      // An expiry in the past cannot happen from the duration list; it lands in the alert.
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bans.newTitle')}</DialogTitle>
          <DialogDescription>{t('bans.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <FormAlert message={form.formState.errors.root?.server?.message} />
            <FormField
              control={form.control}
              name="target"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('bans.target')}</FormLabel>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-wrap gap-4"
                  >
                    {(['visitor', 'ip'] as const).map((value) => (
                      <div key={value} className="flex items-center gap-2">
                        <RadioGroupItem value={value} id={`ban-target-${value}`} />
                        <Label htmlFor={`ban-target-${value}`} className="font-normal">
                          {t(`bans.targets.${value}`)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormItem>
              )}
            />
            {target === 'visitor' ? (
              <TextField
                control={form.control}
                name="visitorId"
                label={t('bans.visitorId')}
                description={t('bans.visitorIdHint')}
                mono
              />
            ) : (
              <TextField
                control={form.control}
                name="ip"
                label={t('bans.ip')}
                description={t('bans.ipHint')}
                placeholder="203.0.113.7"
                mono
              />
            )}
            <TextField
              control={form.control}
              name="reason"
              label={t('bans.reason')}
              description={t('bans.reasonHint')}
              multiline
            />
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('bans.duration')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-56">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BAN_DURATIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`bans.durations.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('common:actions.saving') : t('bans.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
